import { useMutation, useQuery } from '@tanstack/react-query';

import { ApiClientError } from '@/lib/api';
import {
    disconnectSocialAccountAuthorization,
    getOrganizationSocialAccountSourceLimit,
    listOrganizationSocialAccounts,
    reconnectSocialAccountAuthorization,
    startSocialAccountConnect,
    type SocialAccountProvider
} from '@/features/social-accounts/services/social-accounts';

export const socialAccountKeys = {
    all: ['social-accounts'] as const,
    organizations: () => [...socialAccountKeys.all, 'organizations'] as const,
    organization: (organizationId: string) => [...socialAccountKeys.organizations(), organizationId] as const,
    sourceLimit: (organizationId: string) =>
        [...socialAccountKeys.organization(organizationId), 'source-limit'] as const,
    sourceLimitPlaceholder: () => [...socialAccountKeys.all, 'source-limit', 'no-organization'] as const
};

export function useOrganizationSocialAccounts(organizationId: string | null) {
    return useQuery({
        queryKey: organizationId ? socialAccountKeys.organization(organizationId) : socialAccountKeys.all,
        queryFn: () => listOrganizationSocialAccounts(organizationId!),
        enabled: Boolean(organizationId)
    });
}

export function useOrganizationSocialAccountSourceLimit(organizationId: string | null) {
    return useQuery({
        queryKey: organizationId
            ? socialAccountKeys.sourceLimit(organizationId)
            : socialAccountKeys.sourceLimitPlaceholder(),
        queryFn: () => getOrganizationSocialAccountSourceLimit(organizationId!),
        enabled: Boolean(organizationId),
        staleTime: 0
    });
}

export function useStartSocialAccountConnect(organizationId: string | null) {
    return useMutation({
        mutationFn: async (provider: SocialAccountProvider) => {
            if (!organizationId) {
                throw new ApiClientError({ message: 'No organization selected', status: 400 });
            }

            return startSocialAccountConnect({ organizationId, provider });
        }
    });
}

export function useDisconnectSocialAccountAuthorization(organizationId: string | null) {
    return useMutation({
        mutationFn: async ({
            socialAccountId,
            authorizationId
        }: {
            socialAccountId: string;
            authorizationId: string;
        }) => {
            if (!organizationId) {
                throw new ApiClientError({ message: 'No organization selected', status: 400 });
            }

            return disconnectSocialAccountAuthorization({ organizationId, socialAccountId, authorizationId });
        }
    });
}

export function useReconnectSocialAccountAuthorization(organizationId: string | null) {
    return useMutation({
        mutationFn: async ({
            socialAccountId,
            authorizationId
        }: {
            socialAccountId: string;
            authorizationId: string;
        }) => {
            if (!organizationId) {
                throw new ApiClientError({ message: 'No organization selected', status: 400 });
            }

            return reconnectSocialAccountAuthorization({ organizationId, socialAccountId, authorizationId });
        }
    });
}
